/**
 * TELA 6 — CAMPANHAS
 *
 * A arquitetura de informação é a do protótipo (`incubadora.html`,
 * `viewCampanhas` + `detalheCampanha`): faixa de indicadores, o aviso de que
 * a Incubadora é o portão, a tabela com a barra de funil, e o detalhe aberto
 * abaixo dela.
 *
 * O que esta tela acrescenta ao protótipo é o **planejador**: a campanha
 * deixou de ser "escolhi números e mandei" e passou a ser uma equação com
 * quatro variáveis (volume = números × taxa × dias) onde o operador trava uma
 * e o sistema resolve as outras — e diz, em português, se cabe.
 *
 * Duas regras atravessam o arquivo inteiro:
 *  · a soma do funil é a base total (invariante 1) — quando `integro` é falso
 *    a tela GRITA, porque contato perdido em silêncio é o pior defeito
 *    possível neste produto;
 *  · todo veredito carrega o motivo textual que a API mandou. Nunca só cor,
 *    nunca só número.
 */
import { useCallback, useMemo, useState } from "react";
import type {
  CampanhaResumo,
  DiaDoCronograma,
  EstadoCampanha,
  EstadoNumero,
  Horizonte,
  Kit,
  ModoCta,
  ModoPlano,
  NivelMidia,
  NumeroResumo,
  PlanoCampanha,
} from "@co/contracts";
import { api, type CampanhaDetalhe, type RespostaPainel } from "../api.js";
import { irPara } from "../Casca.js";
import { PassoDaBase, lerResumo, type ResumoBase } from "./PassoDaBase.js";
import {
  Aviso,
  Botao,
  Campo,
  Indicador,
  Painel,
  Selo,
  Tabela,
  classeEntrada,
  type Tom,
} from "../ui/base.js";
import {
  BarraFunil,
  Conteudo,
  Escolha,
  Gaveta,
  Grade,
  Indicadores,
  LegendaFunil,
  Leitura,
  LinhaVazia,
  Marcar,
  Td,
  Veredito,
  fmtDataHora,
  fmtDia,
  fmtNum,
  idDaResposta,
  inteiro,
  objeto,
  pctDe,
  texto,
  useCarga,
} from "../ui/comercial.js";

/* ══════════════════════════════════════════════════════════════════════
   Vocabulário visual
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Tom por estado. `ember` é atividade, `ok` é desfecho bom, `warn` é atenção,
 * `crit` é risco, `idle` é parado — a mesma leitura nos três temas.
 */
const TOM_CAMPANHA: Record<EstadoCampanha, Tom> = {
  rascunho: "idle",
  aprovada: "cool",
  rodando: "ember",
  pausada: "warn",
  concluida: "ok",
  abortada: "crit",
};

const TOM_NUMERO: Record<EstadoNumero, Tom> = {
  novo: "idle",
  aquecendo: "ember",
  apto: "ok",
  suspenso: "warn",
  banido: "crit",
};

const TOM_DIA: Record<DiaDoCronograma["estado"], Tom> = {
  planejado: "idle",
  em_execucao: "ember",
  concluido: "ok",
  replanejado: "warn",
  cancelado: "crit",
};

const ROTULO_DIA: Record<DiaDoCronograma["estado"], string> = {
  planejado: "planejado",
  em_execucao: "em execução",
  concluido: "concluído",
  replanejado: "replanejado",
  cancelado: "cancelado",
};

const ROTULO_MIDIA: Record<NivelMidia, string> = {
  texto: "só texto",
  texto_imagem: "texto e imagem",
  completo: "completo (áudio, vídeo, documento)",
};

/** Do menos exigente ao mais exigente. Usado para medir o parque disponível. */
const ESCADA_MIDIA: readonly NivelMidia[] = ["texto", "texto_imagem", "completo"];

const ROTULO_CTA: Record<ModoCta, string> = {
  nenhum: "sem chamada — a resposta é o objetivo",
  numero_em_texto: "número escrito no texto",
  link_wa: "link wa.me",
};

/* ══════════════════════════════════════════════════════════════════════
   Tela
   ══════════════════════════════════════════════════════════════════════ */

interface Contexto {
  campanhas: CampanhaResumo[];
  /** O painel é a única fonte de capacidade agregada e base contactável. */
  painel: RespostaPainel | null;
}

export function Campanhas(): JSX.Element {
  const [aberta, setAberta] = useState<string | null>(null);
  const [construindo, setConstruindo] = useState(false);

  // A capacidade do parque não é opcional para decidir, mas também não pode
  // derrubar a lista de campanhas: se o painel falhar, a tela segue sem ele.
  const buscar = useCallback(
    async (): Promise<Contexto> => {
      const [cs, pnl] = await Promise.all([
        api.campanhas(),
        api.painel().catch((): null => null),
      ]);
      return { campanhas: cs.itens, painel: pnl };
    },
    [],
  );

  const { estado, recarregar } = useCarga(buscar);

  return (
    <div className="grid gap-4">
      <Conteudo estado={estado} aoRecarregar={recarregar}>
        {(ctx) => (
          <ListaDeCampanhas
            ctx={ctx}
            aberta={aberta}
            aoAbrir={setAberta}
            aoConstruir={() => setConstruindo(true)}
            aoRecarregar={recarregar}
          />
        )}
      </Conteudo>

      {aberta && (
        <DetalheDaCampanha
          id={aberta}
          aoFechar={() => setAberta(null)}
          aoMudar={recarregar}
        />
      )}

      {construindo && (
        <Construtor
          aoFechar={() => setConstruindo(false)}
          aoConcluir={(id) => {
            setConstruindo(false);
            setAberta(id);
            recarregar();
          }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Lista
   ══════════════════════════════════════════════════════════════════════ */

function ListaDeCampanhas({
  ctx,
  aberta,
  aoAbrir,
  aoConstruir,
  aoRecarregar,
}: {
  ctx: Contexto;
  aberta: string | null;
  aoAbrir: (id: string) => void;
  aoConstruir: () => void;
  aoRecarregar: () => void;
}): JSX.Element {
  const [agindo, setAgindo] = useState<string | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);

  const cs = useMemo(
    () =>
      ctx.campanhas
        .slice()
        .sort((a, b) => b.criadaEm.localeCompare(a.criadaEm)),
    [ctx.campanhas],
  );

  const soma = cs.reduce(
    (a, c) => ({
      base: a.base + c.funil.total,
      enviados: a.enviados + c.funil.enviado,
      pendentes: a.pendentes + c.funil.pendente,
      respostas: a.respostas + c.funil.responderam,
    }),
    { base: 0, enviados: 0, pendentes: 0, respostas: 0 },
  );

  const rodando = cs.filter((c) => c.estado === "rodando").length;
  const inconsistentes = cs.filter((c) => !c.funil.integro).length;
  const parque = ctx.painel?.parque ?? null;

  async function agir(
    id: string,
    acao: (id: string) => Promise<unknown>,
  ): Promise<void> {
    setAgindo(id);
    setErroAcao(null);
    try {
      await acao(id);
      aoRecarregar();
    } catch (e: unknown) {
      setErroAcao(e instanceof Error ? e.message : "A ação não foi aceita.");
    } finally {
      setAgindo(null);
    }
  }

  return (
    <div className="grid gap-4">
      <Indicadores>
        <Indicador
          rotulo="Campanhas"
          valor={cs.length}
          nota={`${rodando} rodando agora`}
        />
        <Indicador
          rotulo="Base total"
          valor={fmtNum(soma.base)}
          nota={`${fmtNum(soma.pendentes)} ainda pendentes`}
        />
        <Indicador
          rotulo="Disparados"
          valor={fmtNum(soma.enviados)}
          nota={`${pctDe(soma.respostas, soma.enviados)}% responderam`}
        />
        <Indicador
          rotulo="Capacidade hoje"
          valor={parque ? fmtNum(parque.capacidadeHoje) : "—"}
          nota={
            parque
              ? `${parque.aptos} número(s) apto(s)`
              : "painel indisponível agora"
          }
        />
      </Indicadores>

      {/* A Incubadora é o portão: sem número liberado, não existe disparo. */}
      {parque && parque.aptos === 0 && (
        <Aviso tom="warn">
          <strong>Nenhum número apto no momento.</strong> Campanha só usa número
          que a Incubadora já liberou — quem decide aptidão é ela, e este módulo
          apenas consome essa lista. Abra <strong>Números</strong> para ver o que
          falta em cada um.
        </Aviso>
      )}

      {inconsistentes > 0 && (
        <Aviso tom="crit">
          <strong>
            {inconsistentes} campanha(s) com base inconsistente.
          </strong>{" "}
          Pendentes + enviados + falhas + ignorados tem de ser sempre igual à
          base total, inclusive depois de pausar, retomar ou redistribuir por
          queda de número. Enquanto não fechar, o número de contatos entregues
          não é confiável.
        </Aviso>
      )}

      {erroAcao && <Aviso tom="crit">{erroAcao}</Aviso>}

      <Painel
        titulo="Campanhas"
        dica={`${cs.length} no total · o histórico não some depois de encerrar`}
        acoes={
          <Botao tipo="principal" pequeno onClick={aoConstruir}>
            Nova campanha
          </Botao>
        }
        semCorpo
      >
        <Tabela
          larguraMin={980}
          colunas={[
            { chave: "nome", rotulo: "Campanha" },
            { chave: "estado", rotulo: "Estado" },
            { chave: "funil", rotulo: "Andamento" },
            { chave: "resp", rotulo: "Respostas", direita: true },
            { chave: "nums", rotulo: "Números", direita: true },
            { chave: "criada", rotulo: "Criada" },
            { chave: "acoes", rotulo: "", direita: true },
          ]}
        >
          {cs.length === 0 ? (
            <LinhaVazia colunas={7}>
              Nenhuma campanha ainda. Crie a primeira com os números que a
              Incubadora liberou.
            </LinhaVazia>
          ) : (
            cs.map((c) => {
              const processados =
                c.funil.enviado + c.funil.falhou + c.funil.ignorado;
              return (
                <tr
                  key={c.id}
                  className={c.id === aberta ? "bg-superficie-2" : ""}
                >
                  <Td>
                    <button
                      type="button"
                      onClick={() => aoAbrir(c.id)}
                      className="text-left font-medium text-tinta hover:text-marca rounded-sm"
                    >
                      {c.nome}
                    </button>
                    <div className="text-[11.5px] text-tinta-3 mt-0.5">
                      {c.produtoNome ?? "sem produto"} ·{" "}
                      {c.responsavelNome ?? "sem responsável"}
                    </div>
                  </Td>
                  <Td>
                    <Selo tom={TOM_CAMPANHA[c.estado]}>{c.estado}</Selo>
                    {!c.funil.integro && (
                      <div className="mt-1">
                        <Selo
                          tom="crit"
                          titulo="A soma do funil não bate com a base total."
                        >
                          base inconsistente
                        </Selo>
                      </div>
                    )}
                  </Td>
                  <Td className="min-w-[200px]">
                    <BarraFunil funil={c.funil} />
                    <div className="text-[11px] text-tinta-3 mt-1 tabular">
                      {fmtNum(processados)} de {fmtNum(c.funil.total)} ·{" "}
                      {pctDe(processados, c.funil.total)}%
                    </div>
                  </Td>
                  <Td direita>{fmtNum(c.funil.responderam)}</Td>
                  <Td direita>{c.numerosUsados}</Td>
                  <Td className="text-tinta-3 whitespace-nowrap text-[11.5px]">
                    {fmtDataHora(c.criadaEm)}
                  </Td>
                  <Td direita>
                    <div className="flex gap-1.5 justify-end flex-wrap">
                      {c.estado === "rodando" && (
                        <Botao
                          pequeno
                          tipo="fantasma"
                          desabilitado={agindo === c.id}
                          onClick={() => {
                            const m = window.prompt(
                              "Por que está pausando? O motivo fica na auditoria.",
                            );
                            if (m && m.trim().length >= 3) {
                              void agir(c.id, (id) =>
                                api.pausarCampanha(id, m.trim()),
                              );
                            }
                          }}
                        >
                          Pausar
                        </Botao>
                      )}
                      {c.estado === "pausada" && (
                        <Botao
                          pequeno
                          tipo="fantasma"
                          desabilitado={agindo === c.id}
                          onClick={() => void agir(c.id, api.retomarCampanha)}
                        >
                          Retomar
                        </Botao>
                      )}
                      <Botao pequeno tipo="fantasma" onClick={() => aoAbrir(c.id)}>
                        Abrir
                      </Botao>
                    </div>
                  </Td>
                </tr>
              );
            })
          )}
        </Tabela>
        <div className="px-5 py-3 border-t border-linha">
          <LegendaFunil />
        </div>
      </Painel>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Detalhe
   ══════════════════════════════════════════════════════════════════════ */

function DetalheDaCampanha({
  id,
  aoFechar,
  aoMudar,
}: {
  id: string;
  aoFechar: () => void;
  aoMudar: () => void;
}): JSX.Element {
  const buscar = useCallback(() => api.campanha(id), [id]);
  const { estado, recarregar } = useCarga(buscar);

  return (
    <Conteudo estado={estado} aoRecarregar={recarregar}>
      {(d) => (
        <CorpoDoDetalhe
          d={d}
          aoFechar={aoFechar}
          aoMudar={() => {
            recarregar();
            aoMudar();
          }}
        />
      )}
    </Conteudo>
  );
}

function CorpoDoDetalhe({
  d,
  aoFechar,
  aoMudar,
}: {
  d: CampanhaDetalhe;
  aoFechar: () => void;
  aoMudar: () => void;
}): JSX.Element {
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [editando, setEditando] = useState(false);
  const [nomeNovo, setNomeNovo] = useState("");
  const c = d.campanha;
  const f = c.funil;
  const processados = f.enviado + f.falhou + f.ignorado;
  const somaFunil = processados + f.pendente;

  async function agir(acao: () => Promise<unknown>): Promise<void> {
    setOcupado(true);
    setErro(null);
    try {
      await acao();
      aoMudar();
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : "A ação não foi aceita.");
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="grid gap-4">
      {erro && <Aviso tom="crit">{erro}</Aviso>}

      {editando && (
        <Gaveta
          titulo={`Editar ${c.nome}`}
          dica="Em rascunho o nome muda livremente; produto e kit exigem campanha nova depois disso"
          aoFechar={() => setEditando(false)}
          rodape={
            <>
              <Botao
                tipo="principal"
                desabilitado={ocupado || nomeNovo.trim().length < 2}
                onClick={() =>
                  void agir(async () => {
                    await api.editarCampanha(c.id, { nome: nomeNovo.trim() });
                    setEditando(false);
                  })
                }
              >
                Salvar
              </Botao>
              <Botao tipo="fantasma" onClick={() => setEditando(false)}>
                Cancelar
              </Botao>
            </>
          }
        >
          <div className="grid gap-4">
            <Campo rotulo="Nome da campanha">
              <input
                className={classeEntrada}
                defaultValue={c.nome}
                onChange={(e) => setNomeNovo(e.target.value)}
              />
            </Campo>
            <Aviso tom="cool">
              Produto e kit não aparecem aqui de propósito. Depois que a base
              entrou, os alvos já passaram pela supressão contra o produto
              atual — trocá-lo dispararia uma oferta que a base nunca foi
              conferida contra. Para outro produto, crie outra campanha.
            </Aviso>
          </div>
        </Gaveta>
      )}

      {/* ── Situação ─────────────────────────────────────────────── */}
      <Painel
        titulo={c.nome}
        dica={`${c.produtoNome ?? "sem produto"} · criada em ${fmtDataHora(c.criadaEm)}`}
        acoes={
          <>
            {c.estado === "rascunho" && (
              <Botao
                pequeno
                desabilitado={ocupado}
                onClick={() => void agir(() => api.aprovarCampanha(c.id))}
              >
                Aprovar
              </Botao>
            )}
            {(c.estado === "rodando" || c.estado === "aprovada") && (
              <Botao
                pequeno
                desabilitado={ocupado}
                onClick={() => {
                  const m = window.prompt(
                    "Por que está pausando? O motivo fica na auditoria.",
                  );
                  if (m && m.trim().length >= 3) {
                    void agir(() => api.pausarCampanha(c.id, m.trim()));
                  }
                }}
              >
                Pausar
              </Botao>
            )}
            {c.estado === "pausada" && (
              <Botao
                pequeno
                desabilitado={ocupado}
                onClick={() => void agir(() => api.retomarCampanha(c.id))}
              >
                Retomar
              </Botao>
            )}
            {c.estado === "rascunho" && (
              <Botao pequeno tipo="fantasma" onClick={() => setEditando(true)}>
                Editar
              </Botao>
            )}
            {c.estado !== "abortada" && c.estado !== "concluida" && (
              <Botao
                pequeno
                tipo="fantasma"
                desabilitado={ocupado}
                titulo="Encerra a campanha; os pendentes viram ignorados com motivo"
                onClick={() => {
                  const m = window.prompt(
                    "Abortar encerra a campanha. Os contatos pendentes viram ignorados, com este motivo registrado:",
                  );
                  if (m && m.trim().length >= 3) {
                    void agir(() => api.abortarCampanha(c.id, m.trim()));
                  }
                }}
              >
                Abortar
              </Botao>
            )}
            <Botao
              pequeno
              tipo="perigo"
              desabilitado={ocupado}
              titulo="Só funciona em campanha que nunca tocou ninguém"
              onClick={() => {
                if (
                  window.confirm(
                    `Excluir "${c.nome}" de vez?

Só é possível se ela nunca tocou ninguém. Se já disparou, o servidor recusa e o caminho é abortar.`,
                  )
                ) {
                  void agir(async () => {
                    await api.excluirCampanha(c.id);
                    aoFechar();
                  });
                }
              }}
            >
              Excluir
            </Botao>
            <Botao pequeno tipo="fantasma" onClick={aoFechar}>
              Fechar
            </Botao>
          </>
        }
      >
        <Grade>
          <Leitura rotulo="Estado">
            <Selo tom={TOM_CAMPANHA[c.estado]}>{c.estado}</Selo>
          </Leitura>
          <Leitura rotulo="Base total">{fmtNum(f.total)}</Leitura>
          <Leitura rotulo="Processados">
            {fmtNum(processados)} — {fmtNum(f.enviado)} enviados,{" "}
            {fmtNum(f.falhou)} falhas, {fmtNum(f.ignorado)} ignorados
          </Leitura>
          <Leitura rotulo="Pendentes">{fmtNum(f.pendente)}</Leitura>
          <Leitura rotulo="Responderam">
            {fmtNum(f.responderam)} ({pctDe(f.responderam, f.enviado)}% do
            enviado)
          </Leitura>
          <Leitura rotulo="Responsável">{c.responsavelNome ?? "—"}</Leitura>
        </Grade>

        <div className="mt-4 grid gap-2">
          <BarraFunil funil={f} />
          <LegendaFunil />
        </div>

        <div className="mt-4">
          {f.integro ? (
            <Aviso tom="ok">
              <strong>Base íntegra:</strong> {fmtNum(somaFunil)} de{" "}
              {fmtNum(f.total)} contatos localizados. Pendentes + enviados +
              falhas + ignorados é sempre igual à base total — inclusive depois
              de pausar, retomar ou redistribuir por queda de número.
            </Aviso>
          ) : (
            <Aviso tom="crit">
              <strong>Base inconsistente:</strong> {fmtNum(somaFunil)} contatos
              somados contra {fmtNum(f.total)} na base — diferença de{" "}
              {fmtNum(Math.abs(f.total - somaFunil))}. Isso nunca deveria
              acontecer: enquanto não fechar, não trate os números desta
              campanha como entrega comprovada.
            </Aviso>
          )}
        </div>
      </Painel>

      {/* ── O plano ──────────────────────────────────────────────── */}
      <BlocoDoPlano plano={d.plano} />

      {/* ── Cronograma ───────────────────────────────────────────── */}
      <BlocoCronograma dias={d.cronograma} />

      {/* ── Distribuição por número ──────────────────────────────── */}
      <BlocoPorNumero itens={d.porNumero} />

      {/* ── Variações ────────────────────────────────────────────── */}
      <BlocoVariacoes itens={d.variacoes} />
    </div>
  );
}

/** O veredito do planejador, gravado na aprovação para auditar depois. */
function BlocoDoPlano({ plano }: { plano: PlanoCampanha | null }): JSX.Element {
  if (!plano) {
    return (
      <Painel titulo="Plano" dica="O que o planejador calculou">
        <Aviso tom="warn">
          Esta campanha ainda não foi planejada. Sem plano não há veredito de
          viabilidade nem cronograma — o disparo sairia sem ninguém ter
          respondido se a capacidade do parque comporta o volume.
        </Aviso>
      </Painel>
    );
  }

  const travado: Array<[string, string]> = [];
  if (plano.volumeContatos !== null)
    travado.push(["Volume em contatos", fmtNum(plano.volumeContatos)]);
  if (plano.volumeMensagens !== null)
    travado.push(["Volume em mensagens", fmtNum(plano.volumeMensagens)]);
  if (plano.numerosFixados !== null)
    travado.push(["Números fixados", String(plano.numerosFixados)]);
  if (plano.taxaPorNumero !== null)
    travado.push(["Taxa por aparelho/dia", String(plano.taxaPorNumero)]);
  if (plano.prazoDias !== null)
    travado.push(["Prazo em dias", String(plano.prazoDias)]);

  return (
    <Painel
      titulo="Plano"
      dica={`modo ${plano.modo.replace(/_/g, " ")} · horizonte ${plano.horizonte} · ${plano.inicio} a ${plano.fim}`}
    >
      <Veredito
        tom={plano.viavel ? "ok" : "crit"}
        titulo={
          plano.viavel
            ? `Cabe: ${plano.diasNecessarios} dia(s) com o parque de hoje`
            : `Não cabe: faltam ${plano.deficitNumeros} número(s) aptos`
        }
        motivo={plano.recomendacao}
        detalhes={
          plano.alternativas.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.08em] opacity-80">
                Outros caminhos
              </div>
              <ul className="mt-1 grid gap-1 text-[13px] leading-relaxed">
                {plano.alternativas.map((a) => (
                  <li key={a} className="flex gap-2">
                    <span aria-hidden="true">·</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )
        }
      />

      <div className="mt-4 grid gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tinta-3 mb-2">
            O que o operador travou
          </div>
          {travado.length === 0 ? (
            <p className="text-[13px] text-tinta-3">
              Nenhuma variável travada neste modo.
            </p>
          ) : (
            <Grade>
              {travado.map(([r, v]) => (
                <Leitura key={r} rotulo={r}>
                  {v}
                </Leitura>
              ))}
            </Grade>
          )}
        </div>

        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tinta-3 mb-2">
            A oferta apurada no momento do cálculo
          </div>
          <Grade>
            <Leitura rotulo="Números aptos">{plano.numerosAptos}</Leitura>
            <Leitura rotulo="Teto agregado por dia">
              {fmtNum(plano.tetoAgregadoDia)} mensagens
            </Leitura>
            <Leitura rotulo="Base contactável">
              {fmtNum(plano.baseContactavel)}
            </Leitura>
            <Leitura rotulo="Dias de base restante">
              {plano.diasDeBaseRestante === null
                ? "—"
                : `${plano.diasDeBaseRestante} dia(s)`}
            </Leitura>
            <Leitura rotulo="Volume possível">
              {fmtNum(plano.volumePossivel)}
            </Leitura>
            <Leitura rotulo="Dias necessários">{plano.diasNecessarios}</Leitura>
            <Leitura rotulo="Déficit de números">
              {plano.deficitNumeros}
            </Leitura>
            <Leitura rotulo="Chips a comprar">{plano.chipsAComprar}</Leitura>
            <Leitura rotulo="Lead time do chip">
              {plano.leadTimeDias === null
                ? "—"
                : `${plano.leadTimeDias} dia(s) até ficar apto`}
            </Leitura>
          </Grade>
        </div>
      </div>
    </Painel>
  );
}

/**
 * Cronograma dia a dia. Planejado × realizado lado a lado, e o motivo quando
 * o dia foi replanejado — porque o cronograma compromete VOLUME, nunca chip:
 * se um número cai na terça, quarta a sexta é recalculada sobre a capacidade
 * que sobrou, e o operador precisa ver o que mudou e por quê.
 */
function BlocoCronograma({ dias }: { dias: DiaDoCronograma[] }): JSX.Element {
  const maior = dias.reduce(
    (m, d) => Math.max(m, d.volumePlanejado, d.volumeRealizado),
    0,
  );
  const replanejados = dias.filter((d) => d.estado === "replanejado").length;

  return (
    <Painel
      titulo="Cronograma"
      dica={
        replanejados > 0
          ? `${dias.length} dia(s) · ${replanejados} replanejado(s)`
          : `${dias.length} dia(s) programado(s)`
      }
      semCorpo
    >
      <Tabela
        larguraMin={860}
        colunas={[
          { chave: "dia", rotulo: "Dia" },
          { chave: "barra", rotulo: "Planejado × realizado" },
          { chave: "plan", rotulo: "Planejado", direita: true },
          { chave: "real", rotulo: "Realizado", direita: true },
          { chave: "nums", rotulo: "Números", direita: true },
          { chave: "taxa", rotulo: "Taxa/aparelho", direita: true },
          { chave: "estado", rotulo: "Situação" },
        ]}
      >
        {dias.length === 0 ? (
          <LinhaVazia colunas={7}>
            Sem cronograma. Ele nasce quando a campanha é planejada — uma linha
            por dia do horizonte escolhido.
          </LinhaVazia>
        ) : (
          dias.map((d) => (
            <tr key={d.dia}>
              <Td className="whitespace-nowrap font-mono text-[12.5px]">
                {fmtDia(d.dia)}
              </Td>
              <Td className="min-w-[180px]">
                <div className="grid gap-1">
                  <div
                    className="h-2 rounded-sm bg-superficie-3 overflow-hidden"
                    title={`Planejado: ${d.volumePlanejado}`}
                  >
                    <div
                      className="h-full bg-marca-suave"
                      style={{
                        width: `${maior > 0 ? (d.volumePlanejado / maior) * 100 : 0}%`,
                      }}
                    />
                  </div>
                  <div
                    className="h-2 rounded-sm bg-superficie-3 overflow-hidden"
                    title={`Realizado: ${d.volumeRealizado}`}
                  >
                    <div
                      className="h-full bg-ok"
                      style={{
                        width: `${maior > 0 ? (d.volumeRealizado / maior) * 100 : 0}%`,
                      }}
                    />
                  </div>
                </div>
              </Td>
              <Td direita>{fmtNum(d.volumePlanejado)}</Td>
              <Td direita>{fmtNum(d.volumeRealizado)}</Td>
              <Td direita>{d.numerosPrevistos}</Td>
              <Td direita>{d.taxaPrevista}</Td>
              <Td>
                <Selo tom={TOM_DIA[d.estado]}>{ROTULO_DIA[d.estado]}</Selo>
                {/* Sem o motivo, "replanejado" é só uma etiqueta amarela. */}
                {d.motivoReplano && (
                  <p className="text-[11.5px] text-tinta-2 mt-1 leading-relaxed max-w-[42ch]">
                    {d.motivoReplano}
                  </p>
                )}
              </Td>
            </tr>
          ))
        )}
      </Tabela>
    </Painel>
  );
}

function BlocoPorNumero({
  itens,
}: {
  itens: CampanhaDetalhe["porNumero"];
}): JSX.Element {
  const inelegiveis = itens.filter((n) => !n.elegivel).length;
  return (
    <Painel
      titulo="Distribuição entre os números"
      dica={
        inelegiveis > 0
          ? `${itens.length} número(s) · ${inelegiveis} fora da distribuição agora`
          : "Quem cai sai da distribuição e os pendentes dele voltam para a fila"
      }
      semCorpo
    >
      <Tabela
        larguraMin={880}
        colunas={[
          { chave: "num", rotulo: "Número" },
          { chave: "estado", rotulo: "Estado na Incubadora" },
          { chave: "env", rotulo: "Enviadas", direita: true },
          { chave: "pend", rotulo: "Pendentes", direita: true },
          { chave: "uso", rotulo: "Uso hoje", direita: true },
          { chave: "sit", rotulo: "Situação e motivo" },
        ]}
      >
        {itens.length === 0 ? (
          <LinhaVazia colunas={6}>
            Nenhum número atribuído a esta campanha.
          </LinhaVazia>
        ) : (
          itens.map((n) => (
            <tr key={n.numeroId}>
              <Td className="font-mono text-[12.5px] whitespace-nowrap">
                {n.telefoneExibicao}
              </Td>
              <Td>
                <Selo tom={TOM_NUMERO[n.estado]}>{n.estado}</Selo>
              </Td>
              <Td direita>{fmtNum(n.enviadas)}</Td>
              <Td direita>{fmtNum(n.pendentes)}</Td>
              <Td direita>
                {n.usoHoje} / {n.teto}
              </Td>
              <Td>
                <Selo tom={n.elegivel ? "ok" : "warn"}>
                  {n.elegivel ? "recebendo" : "fora da distribuição"}
                </Selo>
                {/* O motivo vem da API tanto no sim quanto no não. */}
                <p className="text-[11.5px] text-tinta-2 mt-1 leading-relaxed max-w-[44ch]">
                  {n.motivo}
                </p>
              </Td>
            </tr>
          ))
        )}
      </Tabela>
    </Painel>
  );
}

/**
 * Métrica por variação. Bloqueio e denúncia por variação são o sinal precoce
 * de que o TEXTO está queimando o chip — e o texto, não a cadência, é o
 * controle primário de anti-ban nesta operação.
 */
function BlocoVariacoes({
  itens,
}: {
  itens: CampanhaDetalhe["variacoes"];
}): JSX.Element {
  return (
    <Painel
      titulo="Variações da mensagem"
      dica="Resposta, bloqueio e denúncia medidos por variação"
      semCorpo
    >
      <Tabela
        larguraMin={900}
        colunas={[
          { chave: "ord", rotulo: "#" },
          { chave: "corpo", rotulo: "Texto" },
          { chave: "env", rotulo: "Enviadas", direita: true },
          { chave: "resp", rotulo: "Respostas", direita: true },
          { chave: "bloq", rotulo: "Bloqueios", direita: true },
          { chave: "den", rotulo: "Denúncias", direita: true },
        ]}
      >
        {itens.length === 0 ? (
          <LinhaVazia colunas={6}>
            Nenhuma variação registrada. Uma redação só, repetida, é a
            assinatura mais fácil de detectar.
          </LinhaVazia>
        ) : (
          itens.map((v) => (
            <tr key={v.id}>
              <Td className="tabular text-tinta-3">{v.ordem}</Td>
              <Td className="max-w-[46ch]">
                <p className="text-[12.5px] text-tinta-2 leading-relaxed whitespace-pre-wrap">
                  {v.corpo}
                </p>
              </Td>
              <Td direita>{fmtNum(v.enviadas)}</Td>
              <Td direita>
                {fmtNum(v.respostas)}
                <div className="text-[11px] text-tinta-3">
                  {pctDe(v.respostas, v.enviadas)}%
                </div>
              </Td>
              <Td direita>
                {v.bloqueios > 0 ? (
                  <Selo tom="warn">{v.bloqueios}</Selo>
                ) : (
                  fmtNum(v.bloqueios)
                )}
              </Td>
              <Td direita>
                {v.denuncias > 0 ? (
                  <Selo tom="crit">{v.denuncias}</Selo>
                ) : (
                  fmtNum(v.denuncias)
                )}
              </Td>
            </tr>
          ))
        )}
      </Tabela>
    </Painel>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Construtor — quatro decisões, não doze campos
   ══════════════════════════════════════════════════════════════════════ */

const PASSOS = [
  { id: 1, nome: "O quê", desc: "produto e kit" },
  { id: 2, nome: "Pra quem", desc: "recorte e supressão" },
  { id: 3, nome: "Como", desc: "formato e chamada" },
  { id: 4, nome: "Quanto", desc: "volume, prazo e chips" },
] as const;

interface Produto {
  id: string;
  nome: string;
  familia: string | null;
}

interface Insumos {
  produtos: Produto[];
  kits: Kit[];
  numeros: NumeroResumo[];
}


/**
 * `POST /api/campanhas/:id/base` ainda não tem schema de resposta no
 * contrato. Em vez de forçar um `as`, a tela lê defensivamente: se o corpo
 * não trouxer o que ela precisa, ela diz que não trouxe em vez de inventar
 * um zero tranquilizador.
 */


const MODOS: ReadonlyArray<{ id: ModoPlano; nome: string; desc: string }> = [
  {
    id: "volume_e_prazo",
    nome: "Volume e prazo",
    desc: "“mil contatos até sexta” — o sistema diz quantos números precisa",
  },
  {
    id: "volume",
    nome: "Só o volume",
    desc: "“mil contatos” — o sistema diz em quantos dias sai",
  },
  {
    id: "capacidade",
    nome: "Chips e taxa",
    desc: "“tenho 10 números a 25 por aparelho” — o sistema diz o alcance",
  },
  {
    id: "ritmo",
    nome: "Só o ritmo",
    desc: "“no máximo 25 por aparelho, custe o que custar”",
  },
];

const HORIZONTES: ReadonlyArray<{ id: Horizonte; nome: string; desc: string }> = [
  { id: "hoje", nome: "Hoje", desc: "programa só o dia corrente" },
  { id: "amanha", nome: "Amanhã", desc: "programa o próximo dia útil" },
  { id: "semana", nome: "Semana", desc: "distribui pelos dias da semana" },
  { id: "mes", nome: "Mês", desc: "distribui pelo mês" },
  { id: "personalizado", nome: "Personalizado", desc: "usa o prazo em dias" },
];

const FORMATOS: ReadonlyArray<{ id: NivelMidia; nome: string; desc: string }> = [
  {
    id: "texto",
    nome: "Só texto",
    desc: "o formato mais barato em capacidade e o que aceita mais números",
  },
  {
    id: "texto_imagem",
    nome: "Texto e imagem",
    desc: "exige número já habilitado a mídia — o parque encolhe",
  },
  {
    id: "completo",
    nome: "Completo",
    desc: "áudio, vídeo e documento; só número no nível mais alto",
  },
];

const CTAS: ReadonlyArray<{ id: ModoCta; nome: string; desc: string }> = [
  {
    id: "nenhum",
    nome: "Sem chamada",
    desc: "a resposta é o objetivo; nada para o modelo inventar",
  },
  {
    id: "numero_em_texto",
    nome: "Número no texto",
    desc: "o contato disca; o valor é injetado, nunca gerado",
  },
  {
    id: "link_wa",
    nome: "Link wa.me",
    desc: "link montado pelo sistema a partir do número cadastrado",
  },
];

function Construtor({
  aoFechar,
  aoConcluir,
}: {
  aoFechar: () => void;
  aoConcluir: (id: string) => void;
}): JSX.Element {
  const buscar = useCallback(async (): Promise<Insumos> => {
    const [p, k, n] = await Promise.all([
      api.produtos(),
      api.kits(),
      api.numeros({ limite: 200 }),
    ]);
    return { produtos: p.itens, kits: k.itens, numeros: n.itens };
  }, []);
  const { estado, recarregar } = useCarga(buscar);

  return (
    <Gaveta
      titulo="Nova campanha"
      dica="Quatro decisões: o quê, pra quem, como e quanto"
      aoFechar={aoFechar}
      largura="larga"
    >
      <Conteudo estado={estado} aoRecarregar={recarregar}>
        {(insumos) => (
          <PassosDoConstrutor insumos={insumos} aoConcluir={aoConcluir} />
        )}
      </Conteudo>
    </Gaveta>
  );
}

function PassosDoConstrutor({
  insumos,
  aoConcluir,
}: {
  insumos: Insumos;
  aoConcluir: (id: string) => void;
}): JSX.Element {
  const [passo, setPasso] = useState(1);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  /* O quê */
  const [nome, setNome] = useState("");
  const [produtoId, setProdutoId] = useState("");

  /* Pra quem */
  const [campanhaId, setCampanhaId] = useState<string | null>(null);
  const [resumoBase, setResumoBase] = useState<ResumoBase | null>(null);
  const [baseEnviada, setBaseEnviada] = useState(false);

  /* Como */
  const [nivelMidia, setNivelMidia] = useState<NivelMidia>("texto");
  const [cta, setCta] = useState<ModoCta>("nenhum");
  const [midiaSeparada, setMidiaSeparada] = useState(false);

  /* Quanto */
  const [modo, setModo] = useState<ModoPlano>("volume_e_prazo");
  const [horizonte, setHorizonte] = useState<Horizonte>("semana");
  const [volumeContatos, setVolumeContatos] = useState("");
  const [prazoDias, setPrazoDias] = useState("");
  const [numerosFixados, setNumerosFixados] = useState("");
  const [taxaPorNumero, setTaxaPorNumero] = useState("");
  const [plano, setPlano] = useState<PlanoCampanha | null>(null);

  const kit = useMemo(
    () => insumos.kits.find((k) => k.produtoId === produtoId) ?? null,
    [insumos.kits, produtoId],
  );


  /**
   * Quantos números do parque atendem ao formato escolhido.
   * Este é o número que faz o operador entender que "formato mais exigente"
   * não é uma preferência estética: ele encolhe o parque disponível.
   */
  const parqueDoFormato = useMemo(() => {
    const exigido = ESCADA_MIDIA.indexOf(nivelMidia);
    const disponiveis = insumos.numeros.filter(
      (n) => n.liberado && n.estado === "apto" && n.pool === "disparo",
    );
    const atendem = disponiveis.filter(
      (n) => ESCADA_MIDIA.indexOf(n.nivelMidia) >= exigido,
    );
    return { total: disponiveis.length, atendem: atendem.length };
  }, [insumos.numeros, nivelMidia]);

  const mensagensPorContato = midiaSeparada && nivelMidia !== "texto" ? 2 : 1;

  /* ── Ações ───────────────────────────────────────────────────── */

  /**
   * Garante que existe rascunho de campanha antes de importar a base.
   *
   * O rascunho nasce aqui, e não no fim, porque a pergunta "quantos foram
   * suprimidos e por quê" só tem resposta depois que o servidor confronta a
   * base com a política de descanso — e essa resposta muda a decisão seguinte.
   */
  async function garantirCampanha(): Promise<string> {
    if (campanhaId) return campanhaId;
    const criada = await api.criarCampanha({
      nome: nome.trim(),
      produtoId,
      kitId: kit?.id ?? null,
    });
    const id = idDaResposta(criada);
    if (!id) {
      throw new Error(
        "A campanha foi criada, mas a resposta não trouxe o identificador. Recarregue a lista antes de tentar de novo — pode ter ficado um rascunho.",
      );
    }
    setCampanhaId(id);
    return id;
  }

  async function calcular(): Promise<void> {
    if (!campanhaId) return;
    setOcupado(true);
    setErro(null);
    try {
      const corpo: Record<string, string | number> = {
        campanhaId,
        modo,
        horizonte,
      };
      const num = (v: string): number | null => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
      };
      if (modo === "volume_e_prazo" || modo === "volume") {
        const v = num(volumeContatos);
        if (v !== null) corpo["volumeContatos"] = v;
      }
      if (modo === "volume_e_prazo" || horizonte === "personalizado") {
        const d = num(prazoDias);
        if (d !== null) corpo["prazoDias"] = d;
      }
      if (modo === "capacidade") {
        const n = num(numerosFixados);
        if (n !== null) corpo["numerosFixados"] = n;
      }
      if (modo === "capacidade" || modo === "ritmo") {
        const t = num(taxaPorNumero);
        if (t !== null) corpo["taxaPorNumero"] = t;
      }
      setPlano(await api.planejar(campanhaId, corpo));
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : "O planejador não respondeu.");
    } finally {
      setOcupado(false);
    }
  }

  /* ── Guardas de avanço ───────────────────────────────────────── */
  const podeSairDo1 = nome.trim().length >= 2 && produtoId !== "";

  /**
   * Depois que o rascunho existe, identidade e base ficam travadas aqui.
   *
   * Não é preciosismo: o contrato não diz se `POST /campanhas/:id/base`
   * substitui ou acrescenta. Reenviar sem saber a resposta arrisca duplicar
   * contatos — e contato duplicado quebra a invariante que diz que a soma do
   * funil é a base total. Ajuste depois, na campanha, onde o efeito é visível.
   */
  const travado = campanhaId !== null;

  return (
    <div className="grid gap-5">
      {/* Trilha dos quatro passos */}
      <ol className="flex gap-2 flex-wrap" aria-label="Etapas da criação">
        {PASSOS.map((p) => {
          const atual = p.id === passo;
          const feito = p.id < passo;
          return (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => {
                  if (p.id < passo) setPasso(p.id);
                }}
                disabled={p.id > passo}
                aria-current={atual ? "step" : undefined}
                className={`text-left rounded-lg px-3 py-2 border transition-colors disabled:cursor-not-allowed ${
                  atual
                    ? "border-marca bg-marca-suave text-marca-tinta"
                    : feito
                      ? "border-linha bg-superficie-2 text-tinta-2 hover:bg-superficie-3"
                      : "border-linha bg-superficie-2 text-tinta-3"
                }`}
              >
                <span className="block text-[12.5px] font-medium">
                  {p.id}. {p.nome}
                </span>
                <span className="block text-[11px] opacity-80">{p.desc}</span>
              </button>
            </li>
          );
        })}
      </ol>

      {erro && <Aviso tom="crit">{erro}</Aviso>}

      {/* ── 1. O QUÊ ─────────────────────────────────────────────── */}
      {passo === 1 && (
        <div className="grid gap-4">
          <Grade colunas={2}>
            <Campo rotulo="Nome da campanha" dica="Como ela aparece na lista e no histórico">
              <input
                className={`${classeEntrada} disabled:opacity-60`}
                value={nome}
                disabled={travado}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex.: Link dedicado — carteira fria agosto"
              />
            </Campo>
            <Campo
              rotulo="Produto"
              dica="O produto puxa o kit: variações, mídias e chamada já montadas"
            >
              <select
                className={`${classeEntrada} disabled:opacity-60`}
                value={produtoId}
                disabled={travado}
                onChange={(e) => setProdutoId(e.target.value)}
              >
                <option value="">Escolha o produto…</option>
                {insumos.produtos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                    {p.familia ? ` — ${p.familia}` : ""}
                  </option>
                ))}
              </select>
            </Campo>
          </Grade>

          {travado && (
            <Aviso tom="cool">
              O rascunho já foi criado com este nome e este produto, então os
              dois ficam travados aqui. Para trocar, abra a campanha depois de
              sair deste assistente.
            </Aviso>
          )}

          {insumos.produtos.length === 0 && (
            <Aviso tom="crit">
              <div className="grid gap-2.5">
                <span>
                  Nenhum produto cadastrado, e sem produto o assistente não
                  segue: é ele que diz o que a campanha oferta, e é ele que a
                  regra de descanso compara para não repetir a mesma oferta ao
                  mesmo contato.
                </span>
                <div>
                  <Botao
                    tipo="principal"
                    pequeno
                    onClick={() => irPara("campanhas/produtos")}
                  >
                    Cadastrar produto
                  </Botao>
                </div>
              </div>
            </Aviso>
          )}

          {produtoId !== "" &&
            (kit ? (
              <Painel titulo="Kit deste produto" dica="Montado uma vez, reutilizado em toda campanha">
                <Grade>
                  <Leitura rotulo="Variações prontas">
                    {kit.variacoes.length}
                  </Leitura>
                  <Leitura rotulo="Mídias no kit">{kit.midias.length}</Leitura>
                  <Leitura rotulo="Chamada">{ROTULO_CTA[kit.cta]}</Leitura>
                  <Leitura rotulo="Nível de mídia exigido">
                    {ROTULO_MIDIA[kit.nivelMidiaExigido]}
                  </Leitura>
                  <Leitura rotulo="Mensagens por contato">
                    {kit.mensagensPorContato}
                  </Leitura>
                </Grade>
              </Painel>
            ) : (
              <Aviso tom="warn">
                Este produto ainda não tem kit. Dá para seguir, mas a campanha
                nasce sem variações — e uma redação só, repetida em toda a base,
                é a assinatura mais fácil de detectar.
              </Aviso>
            ))}

          <div className="flex gap-2">
            <Botao
              tipo="principal"
              desabilitado={!podeSairDo1}
              onClick={() => {
                if (kit) {
                  setCta(kit.cta);
                  setNivelMidia(kit.nivelMidiaExigido);
                  setMidiaSeparada(kit.mensagensPorContato > 1);
                }
                setPasso(2);
              }}
            >
              Continuar
            </Botao>
          </div>
        </div>
      )}

      {/* ── 2. PRA QUEM ──────────────────────────────────────────── */}
      {passo === 2 && (
        <div className="grid gap-4">
          <PassoDaBase
            campanhaId={campanhaId}
            baseEnviada={baseEnviada}
            resumoBase={resumoBase}
            aoGarantirCampanha={garantirCampanha}
            aoImportar={(r) => {
              setResumoBase(r);
              setBaseEnviada(true);
              setPasso(3);
            }}
          />

          <div className="flex gap-2 flex-wrap">
            <Botao tipo="fantasma" onClick={() => setPasso(1)}>
              Voltar
            </Botao>
            {baseEnviada && (
              <Botao tipo="principal" onClick={() => setPasso(3)}>
                Ir para o formato
              </Botao>
            )}
          </div>
        </div>
      )}

      {/* ── 3. COMO ──────────────────────────────────────────────── */}
      {passo === 3 && (
        <div className="grid gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tinta-3 mb-2">
              Formato
            </div>
            <Escolha
              rotuloGrupo="Formato da mensagem"
              valor={nivelMidia}
              aoEscolher={setNivelMidia}
              opcoes={FORMATOS}
            />
          </div>

          {/* A consequência que o operador não vê sozinho: exigir mídia
              elimina do parque todo número que ainda não chegou lá. */}
          <Veredito
            tom={parqueDoFormato.atendem === 0 ? "crit" : parqueDoFormato.atendem < parqueDoFormato.total ? "warn" : "ok"}
            titulo={`${parqueDoFormato.atendem} de ${parqueDoFormato.total} número(s) aptos aceitam este formato`}
            motivo={
              parqueDoFormato.atendem === 0
                ? `Nenhum número liberado está no nível “${ROTULO_MIDIA[nivelMidia]}”. Com este formato a campanha não tem parque para rodar — escolha um formato menos exigente ou espere o aquecimento subir o nível dos números.`
                : parqueDoFormato.atendem < parqueDoFormato.total
                  ? `Exigir “${ROTULO_MIDIA[nivelMidia]}” tira ${parqueDoFormato.total - parqueDoFormato.atendem} número(s) da distribuição. Menos números pelo mesmo volume significa mais mensagens por aparelho — exatamente a variável que queima chip.`
                  : `Todos os números liberados aceitam este formato. O parque não encolhe.`
            }
          />

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tinta-3 mb-2">
              Chamada para ação
            </div>
            <Escolha
              rotuloGrupo="Chamada para ação"
              valor={cta}
              aoEscolher={setCta}
              opcoes={CTAS}
            />
          </div>

          {/* Cicatriz registrada: link inventado pelo modelo entregou página
              morta a pessoas reais. Dado transacional é injetado, nunca gerado. */}
          <Aviso tom="cool">
            <strong>Link, preço e prazo nunca são escritos pelo modelo.</strong>{" "}
            O que aparece na mensagem é injetado pelo sistema a partir do
            cadastro. Onde o texto cala, o modelo inventa — e link morto chega
            igual a quem está do outro lado.
          </Aviso>

          <Painel titulo="Custo em capacidade" dica="Contato não é mensagem">
            <Marcar
              marcado={midiaSeparada}
              desabilitado={nivelMidia === "texto"}
              aoMudar={setMidiaSeparada}
              rotulo="Enviar a imagem e o texto como mensagens separadas"
              dica="Imagem com legenda custa 1 mensagem por contato. Imagem seguida de texto custa 2 — e é a conta de mensagens, não a de contatos, que consome o teto diário de cada número."
            />
            <div className="mt-3 text-[13px] text-tinta-2">
              Custo atual: <strong>{mensagensPorContato}</strong> mensagem(ns)
              por contato.
              {resumoBase && resumoBase.aceitos > 0 && (
                <>
                  {" "}
                  {/* O número que vale é o que sobrou DEPOIS da supressão: é
                      ele que vira alvo e consome teto. O bruto da planilha
                      superestimaria a capacidade necessária. */}
                  {fmtNum(resumoBase.aceitos)} contato(s) na base ={" "}
                  <strong>
                    {fmtNum(resumoBase.aceitos * mensagensPorContato)}
                  </strong>{" "}
                  mensagem(ns) de capacidade.
                </>
              )}
            </div>
          </Painel>

          <div className="flex gap-2 flex-wrap">
            <Botao tipo="fantasma" onClick={() => setPasso(2)}>
              Voltar
            </Botao>
            <Botao tipo="principal" onClick={() => setPasso(4)}>
              Continuar
            </Botao>
          </div>
        </div>
      )}

      {/* ── 4. QUANTO ────────────────────────────────────────────── */}
      {passo === 4 && (
        <div className="grid gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tinta-3 mb-2">
              O que você quer travar
            </div>
            {/* Volume = números × taxa × dias. O operador trava uma variável;
                o sistema resolve as outras e confere contra teto, parque e base. */}
            <Escolha
              rotuloGrupo="Variável travada"
              valor={modo}
              aoEscolher={(v) => {
                setModo(v);
                setPlano(null);
              }}
              opcoes={MODOS}
            />
          </div>

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tinta-3 mb-2">
              Horizonte
            </div>
            <Escolha
              rotuloGrupo="Horizonte do cronograma"
              valor={horizonte}
              aoEscolher={(v) => {
                setHorizonte(v);
                setPlano(null);
              }}
              opcoes={HORIZONTES}
            />
          </div>

          <Grade>
            {(modo === "volume_e_prazo" || modo === "volume") && (
              <Campo rotulo="Volume em contatos" dica="O sistema converte para mensagens pelo formato">
                <input
                  className={classeEntrada}
                  inputMode="numeric"
                  value={volumeContatos}
                  onChange={(e) => setVolumeContatos(e.target.value)}
                  placeholder="1000"
                />
              </Campo>
            )}
            {(modo === "volume_e_prazo" || horizonte === "personalizado") && (
              <Campo rotulo="Prazo em dias">
                <input
                  className={classeEntrada}
                  inputMode="numeric"
                  value={prazoDias}
                  onChange={(e) => setPrazoDias(e.target.value)}
                  placeholder="5"
                />
              </Campo>
            )}
            {modo === "capacidade" && (
              <Campo rotulo="Números fixados">
                <input
                  className={classeEntrada}
                  inputMode="numeric"
                  value={numerosFixados}
                  onChange={(e) => setNumerosFixados(e.target.value)}
                  placeholder="10"
                />
              </Campo>
            )}
            {(modo === "capacidade" || modo === "ritmo") && (
              <Campo
                rotulo="Taxa por aparelho/dia"
                dica="Declarar menos que o teto vale; declarar mais, não — o limite manual é restritivo, nunca ampliador"
              >
                <input
                  className={classeEntrada}
                  inputMode="numeric"
                  value={taxaPorNumero}
                  onChange={(e) => setTaxaPorNumero(e.target.value)}
                  placeholder="25"
                />
              </Campo>
            )}
          </Grade>

          {!campanhaId && (
            <Aviso tom="warn">
              A base ainda não foi conferida. Volte ao passo{" "}
              <strong>Pra quem</strong> e envie os contatos — sem base gravada o
              planejador não tem o que planejar.
            </Aviso>
          )}

          <div className="flex gap-2 flex-wrap">
            <Botao tipo="fantasma" onClick={() => setPasso(3)}>
              Voltar
            </Botao>
            <Botao
              tipo="principal"
              desabilitado={ocupado || !campanhaId}
              onClick={() => void calcular()}
            >
              {ocupado ? "Calculando…" : "Calcular o plano"}
            </Botao>
            {plano && campanhaId && (
              <Botao onClick={() => aoConcluir(campanhaId)}>
                Abrir a campanha
              </Botao>
            )}
          </div>

          {plano && (
            <>
              <Veredito
                tom={plano.viavel ? "ok" : "crit"}
                titulo={
                  plano.viavel
                    ? `Cabe: ${plano.diasNecessarios} dia(s), ${plano.numerosAptos} número(s) apto(s)`
                    : `Não cabe: faltam ${plano.deficitNumeros} número(s) — ${plano.chipsAComprar} chip(s) a comprar`
                }
                motivo={plano.recomendacao}
                detalhes={
                  plano.alternativas.length > 0 && (
                    <ul className="grid gap-1 text-[13px] leading-relaxed">
                      {plano.alternativas.map((a) => (
                        <li key={a} className="flex gap-2">
                          <span aria-hidden="true">·</span>
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  )
                }
              />
              <Grade>
                <Leitura rotulo="Volume possível">
                  {fmtNum(plano.volumePossivel)}
                </Leitura>
                <Leitura rotulo="Teto agregado por dia">
                  {fmtNum(plano.tetoAgregadoDia)}
                </Leitura>
                <Leitura rotulo="Base contactável">
                  {fmtNum(plano.baseContactavel)}
                </Leitura>
                <Leitura rotulo="Dias de base restante">
                  {plano.diasDeBaseRestante === null
                    ? "—"
                    : `${plano.diasDeBaseRestante} dia(s)`}
                </Leitura>
                <Leitura rotulo="Período">
                  {plano.inicio} a {plano.fim}
                </Leitura>
                <Leitura rotulo="Lead time do chip">
                  {plano.leadTimeDias === null
                    ? "—"
                    : `${plano.leadTimeDias} dia(s)`}
                </Leitura>
              </Grade>
            </>
          )}
        </div>
      )}
    </div>
  );
}
